import { useState, useRef, RefObject, FC, ChangeEvent, useEffect } from 'react';
import { env } from 'src/config/env';

import { AuthHelper } from 'src/components/Auth/AuthHelper';
import {
  SpheraButton,
  SpheraModal,
  SpheraDropdown,
  SpheraInput,
  SpheraNotificationGroup,
  SpheraNotification,
  SpheraButtonEx,
} from '@sphera-cloud/component-ui-reactjs';
import { Upload, DetailedError, PreviousUpload } from 'tus-js-client';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import ProgressBar from '../ProgressBar/ProgressBar';
import { FileUploadModalProps } from './types';
import { deleteFile, filesOptionsSelector, optionsFiles, setIsFilesUpload } from 'src/store/slices/files';
import { useAppDispatch, useAppSelector } from 'src/hooks';

const BASIC_WIDTH = 1000;
const BASIC_HEIGHT = 650;

export const FileUploadModal: FC<FileUploadModalProps> = ({ closeModal, showNotification, types }) => {
  const dispatch = useAppDispatch();
  const [height, setHeight] = useState<number | string>(BASIC_HEIGHT);
  const [width, setWidth] = useState<number | string>(BASIC_WIDTH);
  const [maximize, setMaximize] = useState<boolean>(true);
  const [minimize, setMinimize] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [fileType, setFileType] = useState<string>('');
  const [fileExtension, setFileExtension] = useState<string>('');
  const [acceptedFileExtensions, setAcceptedFilesExtensions] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [progressStatus, setProgressStatus] = useState<boolean>(false);
  const [uploadStarted, setUploadStarted] = useState<boolean>(false);
  const [uploadSession, setUploadSession] = useState<any>();
  const [retryAvailable, setRetryAvailable] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const responseDataFileId = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const filesOptions = useAppSelector(filesOptionsSelector);
  const fileInputRef: RefObject<HTMLInputElement> = useRef(null);

  useEffect(() => {
    dispatch(optionsFiles());
  }, []);

  useEffect(() => {
    setFileName(selectedFile?.name ?? '');
  }, [selectedFile?.name]);

  useEffect(() => {
    setAcceptedFilesExtensions(types.map(t=>t.extension));
  }, [types]);

  const validate = () => {
    if (!selectedFile || !fileType) {
      setValidationError('File and file type is required');
      return true;
    } else {
      const lcFileName = selectedFile?.name.toLowerCase() ?? '';
      const lcTypeExtension = fileExtension?.toLowerCase() ?? '';
      if (!lcFileName.endsWith(lcTypeExtension)) {
        setValidationError('File type does not match the selected file');
        return true;
      } else {
        setValidationError('');
        return false;
      }
    }
  };

  const uploadFile = () => {
    if (validate()) {
      return;
    }

    setUploadStarted(true);

    const apiUrl = env.isDev ? env.apiAddress : `${location.origin}/api/v1.0/`;
        const endpoint = `${apiUrl}files?fileDataType=${fileType}&fileName=${
          fileName || selectedFile?.name
          }`;

    const headers = {
      Authorization: `Bearer ${AuthHelper.getAccessToken()}`,
    };

    //#region "override default retry logic"
    // copied this section of the code from tus client library to override default retry logic, with options.onShouldRetry
    function isOnline() {
      let online = true;
      if (typeof window !== 'undefined' &&
        'navigator' in window &&
        window.navigator.onLine === false) {
        online = false;
      }
      return online;
    }
    function inStatusCategory(status: any, category: any) {
      return status >= category && status < category + 100;
    }
    //#endregion "override default retry logic"

    const options = {
      endpoint: endpoint,
      headers,
      chunkSize: filesOptions.tusMaxChunkSize,
      retryDelays: [0, 1000, 3000, 5000],
      onProgress: function (bytesUploaded: number, bytesTotal: number) {
        const progress = Math.floor((bytesUploaded / bytesTotal) * 100);
        setProgressStatus(true);
        setUploadProgress(progress);
        setRetryAvailable(false);
        setError('');
      },
      onShouldRetry: function (err: any) {
        let status = err.originalResponse ? err.originalResponse.getStatus() : 0;
        let isDuplicateFile = err.originalResponse._xhr.responseText.indexOf("DUPLICATE_FILE") > -1;
        return (!inStatusCategory(status, 400) || (status === 409 && !isDuplicateFile) || status === 423) && isOnline();
      },
      onSuccess: function () {
        setProgressStatus(false);
        showNotification('success', 'File uploaded');
        setSelectedFile(null);
        dispatch(setIsFilesUpload(true));
        closeModal();
        setUploadStarted(false);
        setRetryAvailable(false);
        setError('');
      },
      onAfterResponse: function (req: any, res: any) {
        if (res?._xhr?.response && typeof res._xhr.response === 'string') {
          const resJson = JSON.parse(res._xhr.response);
          if (resJson.fileId) responseDataFileId.current = resJson.fileId;
        }
        return res;
      },
      onError: function (error: Error | DetailedError) {
        const originalResponse = (error as DetailedError).originalResponse;
        if (originalResponse) {
          const response = JSON.parse((originalResponse as any)?._xhr.response);
          const errorText = response.errors?.[0]?.message;
          setError(`${response.message} \r\n ${errorText}`);
        } else {
          let errorMessage = error.message;
          if (error.message.includes('invalid or missing length value')) {
            errorMessage = 'File has wrong mimetype';
          }
          setError(errorMessage);
        }
        setRetryAvailable(true);
        if (responseDataFileId.current) {
          dispatch(deleteFile(responseDataFileId.current));
        }
        setUploadStarted(false);
        setProgressStatus(false);
      },
    };

    // @ts-ignore
    const upload = new Upload(selectedFile, options);
    setUploadSession(upload);
    upload.start();
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    setSelectedFile(file);
    setDragOver(false);
  };
  const handleDragOver = (e: any) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const changeFiletype = (e: DropDownListChangeEvent) => {
    setFileType(encodeURIComponent(e.value.mimeType));
    setFileExtension(e.value.extension);
    setAcceptedFilesExtensions([e.value.extension]);
  };

  const handleFilenameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  const retryUpload = () => {
    setRetryAvailable(false);
    if (uploadSession) {
      const getMostRecentUpload = (previousUploads: PreviousUpload[]) => {
        if (previousUploads.length === 0) return null;
        return previousUploads[0];
      };
      uploadSession.findPreviousUploads().then((previousUploads: PreviousUpload[]) => {
        const chosenUpload = getMostRecentUpload(previousUploads);
        if (chosenUpload) {
          uploadSession.resumeFromPreviousUpload(chosenUpload);
        }
        uploadSession.start();
      });
    }
  };

  const cancelFileUpload = () => {
    if (uploadSession) {
      uploadSession.abort();
      setUploadSession(null);
      if (responseDataFileId.current) {
        dispatch(deleteFile(responseDataFileId.current));
      }
    }
    closeModal();
  };

  useEffect(() => {
    if (selectedFile) {
      validate();
    }
  }, [selectedFile, fileType]);

  const isDisabled = () => {
    return uploadStarted || !selectedFile || !fileType || !fileName || validationError;
  };

  return (
    <SpheraModal
      title="Add File"
      height={height}
      width={width}
      actionText="Upload"
      useMaximizeButton={maximize}
      useMinimizeButton={minimize}
      onMaximize={() => {
        setHeight('100%');
        setWidth('100%');
        setMaximize(false);
        setMinimize(true);
      }}
      onMinimize={() => {
        setHeight(BASIC_HEIGHT);
        setWidth(BASIC_WIDTH);
        setMaximize(true);
        setMinimize(false);
      }}
      onAction={uploadFile}
      onCancel={cancelFileUpload}
      onClose={cancelFileUpload}
      actionDisabled={isDisabled()}
    >
      <div className="file-upload-modal" onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
        <div className="file-upload-modal__dropdowns d-flex ">
          <SpheraDropdown
            data={types}
            className="mr-3"
            textField="name"
            dataItemKey="key"
            label="Select File Type"
            disabled={uploadStarted}
            onChange={changeFiletype}
          />
          <SpheraInput
            value={fileName}
            onChange={handleFilenameChange}
            label="File Name"
            placeholder="Name of File"
            disabled={uploadStarted}
          />
        </div>
        <h6>Please upload a new file</h6>
        <div
          className={`file-upload-modal__drag-area ${dragOver ? "drag-over" : validationError ? 'with-error' : ''}`}
          role="button"
          tabIndex={0}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept={`${acceptedFileExtensions.join(', ')}`}
          />
          {error && (
            <SpheraNotificationGroup
              position={{
                top: 70,
                left: '50%',
                transform: 'translateX(-50%) perspective(0)',
              }}
            >
              <SpheraNotification
                closable={true}
                type={'error'}
                text={error}
                onClose={() => setError('')}
              />
            </SpheraNotificationGroup>
          )}
          {progressStatus && (
            <div className="file-upload-modal__progress-bar-container">
              <ProgressBar progress={uploadProgress} />
              {retryAvailable && (
                <SpheraButtonEx
                  primary
                  text="Retry"
                  style={{
                    position: 'absolute',
                    top: 160,
                  }}
                  onClick={retryUpload}
                />
              )}
            </div>
          )}
          {!progressStatus && (
            <div className="file-upload-modal__content">
              {selectedFile && (
                <div className="file-upload-modal__selected-file">
                  Selected File: {selectedFile.name}
                </div>
              )}
              <div className="file-upload-modal__plus-icon" />
              <SpheraButton
                primary="true"
                text="Select File"
                onClick={() => fileInputRef.current?.click()}
              />
              <div className="file-upload-modal__drag-info">Click or Drag and Drop</div>
            </div>
          )}
        </div>
        {validationError && <div className="sphera-error-message">{validationError}</div>}
      </div>
    </SpheraModal>
  );
};
